import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import * as languagesService from '../../services/api/languages';
import * as translationsService from '../../services/api/translations';
import { Language } from '../../entities/language';
import { Translation } from '../../entities/translation';
import { Pagination } from '../../entities/pagination';
import { SimpleObject } from '../../entities/simple-object';
import FilterTranslations from '../../entities/filter/translations';
import { fillFromParams } from '../../services/crud/filter';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import Heading1 from '../../components/common/headings/heading1';
import CommonButtonDefault from '../../components/common/buttons/default';
import TranslationsTableComponent from '../../components/translations/table';
import TranslationsFilter from '../../components/translations/filter';
import PaginationComponent from '../../components/common/pagination';
import { strings } from '../../localization/strings';

function TranslationsListPageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();
  const { token } = useAuth();
  const initialState: FilterTranslations = { page: 0 };
  const [params, setParams] = useState<FilterTranslations>(
    fillFromParams<FilterTranslations>(initialState, queryParams, { isNumeric: ['page'] }),
  );
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [translationResponse, activeLanguages] = await Promise.all([
          translationsService.list(token, { ...params, sort: 'key' }),
          languagesService.list(token),
        ]);

        setTranslations(translationResponse.items);

        setPagination(translationResponse.pagination);
        setLanguages(activeLanguages);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [params, token]);

  const setFilterData = (filterData: FilterTranslations) => {
    const query: SimpleObject = {};

    for (const [key, value] of Object.entries(filterData)) {
      if (typeof value !== 'undefined' && value !== null && value !== '') {
        query[key] = value;
      }
    }

    setParams({ ...filterData, page: 0 });
    setQueryParams({ ...query, page: 0 } as SimpleObject);
  };

  const updatePage = (currentPage: number) => {
    setParams({ ...params, page: currentPage });
    setQueryParams({ ...params, page: currentPage } as SimpleObject);
  };

  const updateTranslation = async (translation: Translation, language: Language, value: string) => {
    const text = { ...translation.text, [language.key]: value };

    await translationsService.update(token, translation.id, { text });
    setTranslations((items) => items.map((item) => (item.id === translation.id ? { ...item, text } : item)));
  };

  return (
    <div>
      <header className="mb-3">
        <div className="flex justify-between space-x-3">
          <Heading1>{strings.translations.title}</Heading1>
          <Link to="/translations/create">
            <CommonButtonDefault type="button" primary>
              {strings.button.create}
            </CommonButtonDefault>
          </Link>
        </div>
      </header>
      <main className="w-full">
        <div className="overflow-hidden rounded-lg border border-gray-200 sm:rounded-lg">
          {error && <AlertPrimary type="danger" text={error} />}
          <TranslationsFilter onUpdate={setFilterData} initialState={initialState} filterState={params} />
          {isLoading ? (
            <CommonAnimatedLoader />
          ) : (
            <>
              {translations.length > 0 && pagination && (
                <>
                  <TranslationsTableComponent
                    translations={translations}
                    languages={languages}
                    onSave={updateTranslation}
                  />
                  <PaginationComponent pagination={pagination} onChangePage={updatePage} />
                </>
              )}
              {translations.length === 0 && <div className="p-4 text-xl sm:p-6 lg:p-8">{strings.search.noResults}</div>}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default TranslationsListPageComponent;
